// src/pages/talleres/editar/InfoBasicaForm.jsx
import React, { useState, useEffect } from 'react';
import { 
  Form, Input, Select, Card, Alert, InputNumber, Divider, 
  Checkbox, Button, Tag, Space, Spin, List 
} from 'antd';
import { 
  InfoCircleOutlined, 
  UserOutlined, 
  SafetyCertificateOutlined,
  TeamOutlined,
  SearchOutlined,
  DeleteOutlined,
  PlusOutlined,
  IdcardOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { TIPOS_ACTIVIDAD, TIPO_DURACION_OPTIONS } from './constants';
import { useNotify } from '../../../../../providers/NotificationProvider';
import Usuarios from '../../../../../service/Usuarios';
import Familiares from '../../../../../service/Familiares';
import MatriculaService from '../../../../../service/Matriculas';

const { TextArea } = Input;

// 🆕 NUEVO: Opciones de intensidad con colores de semáforo
const NIVELES_INTENSIDAD = [
  {
    value: 'baja',
    label: 'Baja',
    descripcion: 'Actividad ligera, se puede conversar con facilidad',
    color: '#0047bb',
    emoji: '🟢'
  },
  {
    value: 'moderada',
    label: 'Moderada',
    descripcion: 'Esfuerzo moderado, algo de sudoración',
    color: '#faad14',
    emoji: '🟡'
  },
  {
    value: 'alta',
    label: 'Alta',
    descripcion: 'Esfuerzo intenso, sudoración abundante',
    color: '#f5222d',
    emoji: '🔴'
  }
];

export default function InfoBasicaForm({ 
  form, 
  recintos, 
  recintosLoading, 
  disabled = false,
  usuariosAutorizados = [],
  setUsuariosAutorizados 
}) {
  const notify = useNotify();
  
  const [esAcademia, setEsAcademia] = useState(false);
  const [verificandoMatricula, setVerificandoMatricula] = useState(false);
  const [configuracionMatricula, setConfiguracionMatricula] = useState(null);
  const [recintoTieneMatricula, setRecintoTieneMatricula] = useState(false);
  const [tipoBusqueda, setTipoBusqueda] = useState('usuario');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [usuarioEncontrado, setUsuarioEncontrado] = useState(null);
  const [familiarEncontrado, setFamiliarEncontrado] = useState(null);
  const [familiaresEncontrados, setFamiliaresEncontrados] = useState([]);

  useEffect(() => {
    const tipoTaller = form.getFieldValue('tipoTaller');
    setEsAcademia(tipoTaller === 'academia');
    
    const recintoId = form.getFieldValue('recinto');
    if (recintoId) {
      verificarMatriculaRecinto(recintoId);
    }
  }, [form]);

  useEffect(() => {
    if (usuariosAutorizados && usuariosAutorizados.length > 0) {
      const normalizado = usuariosAutorizados.map(u => {
        if (u.id && u.tipo) {
          return u;
        }
        
        if (u.cognitoId) {
          return {
            id: u.cognitoId,
            tipo: 'usuario',
            email: u.email,
            rut: u.rut,
            nombre: u.nombre || u.email
          };
        }
        
        if (u._id) {
          return {
            id: u._id,
            tipo: 'familiar',
            rut: u.rut,
            nombre: u.nombre,
            usuarioPrincipal: u.usuarioPrincipal,
            email: u.email || null
          };
        }
        
        return u;
      });
      
      const hayDiferencias = JSON.stringify(normalizado) !== JSON.stringify(usuariosAutorizados);
      if (hayDiferencias) {
        setUsuariosAutorizados(normalizado);
      }
    }
  }, []);

  const verificarMatriculaRecinto = async (recintoId) => {
    if (!recintoId) {
      setRecintoTieneMatricula(false);
      setConfiguracionMatricula(null);
      form.setFieldsValue({ requiereMatricula: false });
      return;
    }
    
    setVerificandoMatricula(true);
    
    try {
      const response = await MatriculaService.VerificarConfiguracionRecinto(recintoId);
      
      if (response.tieneConfiguracion) {
        setRecintoTieneMatricula(true);
        setConfiguracionMatricula(response.configuracion);
      } else {
        setRecintoTieneMatricula(false);
        setConfiguracionMatricula(null);
        form.setFieldsValue({ requiereMatricula: false });
      }
    } catch (error) {
      console.error('Error verificando matrícula:', error);
      setRecintoTieneMatricula(false);
    } finally {
      setVerificandoMatricula(false);
    }
  };

  const handleRecintoChange = (recintoId) => {
    verificarMatriculaRecinto(recintoId);
  };

  const handleTipoTallerChange = (checked) => {
    setEsAcademia(checked);
    const nuevoTipo = checked ? 'academia' : 'libre';
    form.setFieldsValue({ tipoTaller: nuevoTipo });
    
    if (!checked && usuariosAutorizados.length > 0) {
      setUsuariosAutorizados([]);
      notify.info('Lista de autorizados limpiada');
    }
  };

  const buscarEntidad = async () => {
    if (!searchQuery.trim()) {
      notify.warning('Por favor ingresa un criterio de búsqueda');
      return;
    }

    setSearchLoading(true);
    setUsuarioEncontrado(null);
    setFamiliarEncontrado(null);
    setFamiliaresEncontrados([]);

    try {
      if (tipoBusqueda === 'usuario') {
        const usuario = await Usuarios.buscarPorEmailORut(searchQuery.trim());
        
        if (usuario) {
          setUsuarioEncontrado(usuario);
          notify.success(`Usuario encontrado: ${usuario.nombre}`);
        } else {
          notify.error('Usuario no encontrado.');
        }
      } else {
        const response = await Familiares.BuscarPorRutONombre(searchQuery.trim());
        
        if (response && response.length > 0) {
          if (response.length === 1) {
            setFamiliarEncontrado(response[0]);
            notify.success(`Familiar encontrado: ${response[0].nombre}`);
          } else {
            setFamiliaresEncontrados(response);
            notify.info(`Se encontraron ${response.length} familiares.`);
          }
        } else {
          notify.error('Familiar no encontrado.');
        }
      }
    } catch (error) {
      console.error('Error buscando entidad:', error);
      notify.error(error.error || error.message || 'Error al buscar.');
    } finally {
      setSearchLoading(false);
    }
  };

  const agregarEntidad = (entidadSeleccionada = null) => {
    const entidad = entidadSeleccionada || (tipoBusqueda === 'usuario' ? usuarioEncontrado : familiarEncontrado);
    
    if (!entidad) return;

    const id = tipoBusqueda === 'usuario' ? entidad.cognitoId : entidad._id;
    
    const yaExiste = usuariosAutorizados.some(u => u.id === id);

    if (yaExiste) {
      notify.warning('Esta entidad ya está en la lista de autorizados');
      return;
    }

    let nuevaEntidad;
    
    if (tipoBusqueda === 'usuario') {
      nuevaEntidad = {
        id: entidad.cognitoId,
        tipo: 'usuario',
        email: entidad.email,
        rut: entidad.rut,
        nombre: entidad.nombre || entidad.email
      };
    } else {
      nuevaEntidad = {
        id: entidad._id,
        tipo: 'familiar',
        rut: entidad.rut,
        nombre: entidad.nombre,
        usuarioPrincipal: entidad.usuarioPrincipal,
        email: entidad.email || null
      };
    }

    const nuevaLista = [...usuariosAutorizados, nuevaEntidad];
    setUsuariosAutorizados(nuevaLista);
    notify.success(`✅ ${nuevaEntidad.nombre} agregado a la lista`);

    setSearchQuery('');
    setUsuarioEncontrado(null);
    setFamiliarEncontrado(null);
    setFamiliaresEncontrados([]);
  };

  const eliminarEntidad = (id, nombre) => {
    const nuevaLista = usuariosAutorizados.filter(u => u.id !== id);
    setUsuariosAutorizados(nuevaLista);
    notify.info(`${nombre} eliminado de la lista`);
  };

  const formatearMoneda = (monto) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(monto || 0);
  };

  return (
    <div className="space-y-4">
      <Card title="📋 Información básica del taller">
        <Form.Item
          label="Nombre del taller"
          name="nombre"
          rules={[
            { required: true, message: 'El nombre es requerido' },
            { min: 3, message: 'Mínimo 3 caracteres' }
          ]}
        >
          <Input size="large" placeholder="Ej: Yoga para principiantes" />
        </Form.Item>

        <Form.Item
          label="Descripción"
          name="descripcion"
          rules={[
            { required: true, message: 'La descripción es requerida' },
            { min: 10, message: 'Mínimo 10 caracteres' }
          ]}
        >
          <TextArea 
            rows={4} 
            placeholder="Describe el taller, sus objetivos y beneficios..." 
            showCount
            maxLength={500}
          />
        </Form.Item>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item
            label="Recinto"
            name="recinto"
            rules={[{ required: true, message: 'Seleccione un recinto' }]}
          >
            <Select
              size="large"
              options={recintos}
              placeholder="Seleccione recinto"
              loading={recintosLoading}
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              onChange={handleRecintoChange}
            />
          </Form.Item>

          <Form.Item
            label="Tipo de actividad"
            name="tipoActividad"
            rules={[{ required: true, message: 'Seleccione tipo de actividad' }]}
          >
            <Select 
              size="large" 
              options={TIPOS_ACTIVIDAD} 
              placeholder="Seleccione tipo"
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </div>

        {/* 🆕 NUEVO: Campo de Nivel de Intensidad */}
        <Form.Item
          label="Nivel de intensidad"
          name="nivelIntensidad"
          rules={[{ required: true, message: 'Seleccione el nivel de intensidad' }]}
          tooltip="Indica el nivel de esfuerzo físico requerido para esta actividad"
        >
          <Select size="large" placeholder="Seleccione intensidad">
            {NIVELES_INTENSIDAD.map((nivel) => (
              <Select.Option key={nivel.value} value={nivel.value}>
                <div className="flex items-center gap-2">
                  <span 
                    className="inline-block w-3 h-3 rounded-full" 
                    style={{ backgroundColor: nivel.color }}
                  ></span>
                  <span>{nivel.label}</span>
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        

        <Form.Item
          label="Tipo de duración"
          name="tipoDuracionSuscripcion"
          tooltip="Define cómo se calculará la vigencia de las inscripciones"
          rules={[{ required: true, message: 'Seleccione el tipo de duración' }]}
        >
          <Select size="large" options={TIPO_DURACION_OPTIONS} />
        </Form.Item>

        <Form.Item name="tipoTaller" hidden>
          <Input />
        </Form.Item>
      </Card>

      {/* Configuración de Matrícula - código sin cambios */}
      <Card title={
        <span>
          <IdcardOutlined className="mr-2" />
          Matrícula del Recinto
        </span>
      }>
        {verificandoMatricula ? (
          <div className="flex items-center justify-center py-4">
            <Spin tip="Verificando configuración de matrícula..." />
          </div>
        ) : !form.getFieldValue('recinto') ? (
          <Alert
            message="Selecciona un recinto primero"
            description="Para verificar si requiere matrícula, debes seleccionar un recinto en el campo anterior"
            type="info"
            showIcon
          />
        ) : !recintoTieneMatricula ? (
          <Alert
            message="Este recinto no tiene matrícula configurada"
            description={
              <div>
                <p>Este recinto no requiere matrícula para sus talleres.</p>
                <p className="text-xs mt-2">
                  Si deseas configurar matrículas, ve a <strong>Admin → Matrículas → Configuración</strong>
                </p>
              </div>
            }
            type="warning"
            showIcon
          />
        ) : (
          <div className="space-y-4">
            <Alert
              message="✅ Recinto con matrícula configurada"
              description={
                <div>
                  <p><strong>Valor:</strong> {formatearMoneda(configuracionMatricula?.valorMatricula || 0)}</p>
                  <p><strong>Tipo:</strong> {configuracionMatricula?.tipoVigencia === 'anual' ? 'Anual (1 año)' : 'Indefinida'}</p>
                  {configuracionMatricula?.descripcion && (
                    <p className="text-xs mt-1">{configuracionMatricula.descripcion}</p>
                  )}
                </div>
              }
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
            />

            <Form.Item
              name="requiereMatricula"
              valuePropName="checked"
            >
              <Checkbox disabled={disabled}>
                <span className="font-medium">
                  Este taller requiere que los usuarios tengan matrícula vigente
                </span>
              </Checkbox>
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) =>
              prev.requiereMatricula !== curr.requiereMatricula
            }>
              {({ getFieldValue }) => {
                const requiereMatricula = getFieldValue('requiereMatricula');

                if (requiereMatricula) {
                  return (
                    <Alert
                      message="⚠️ Matrícula Obligatoria"
                      description={
                        <div>
                          <p>
                            Solo usuarios con <strong>matrícula vigente</strong> en este recinto podrán inscribirse.
                          </p>
                          <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                            <li>Se validará automáticamente antes de permitir la inscripción</li>
                            <li>Los usuarios sin matrícula verán un aviso para adquirirla primero</li>
                            <li>La matrícula debe estar vigente al momento de la inscripción</li>
                          </ul>
                        </div>
                      }
                      type="warning"
                      showIcon
                    />
                  );
                }

                return (
                  <Alert
                    message="Matrícula No Requerida"
                    description="Los usuarios podrán inscribirse sin necesidad de tener matrícula"
                    type="info"
                    showIcon
                  />
                );
              }}
            </Form.Item>
          </div>
        )}
      </Card>

      {/* Tipo de Taller (Libre vs Academia) - código completo sin cambios desde aquí */}
      <Card title={
        <span>
          <TeamOutlined className="mr-2" />
          Tipo de taller
        </span>
      }>
        <Checkbox 
          checked={esAcademia}
          onChange={(e) => handleTipoTallerChange(e.target.checked)}
          disabled={disabled}
        >
          <span className="font-medium">
            Este es un taller tipo Academia (acceso restringido)
          </span>
        </Checkbox>

        {!esAcademia ? (
          <Alert
            message="Taller Libre"
            description="Este taller estará disponible públicamente para todos los usuarios que cumplan los requisitos"
            type="info"
            showIcon
            className="mt-2"
          />
        ) : (
          <div className="mt-4 space-y-4">
            <Alert
              message="⚠️ Taller tipo Academia"
              description={
                <div className="space-y-2">
                  <p>
                    Este taller <strong>NO aparecerá</strong> en el listado público. 
                    Solo los usuarios y familiares autorizados podrán verlo e inscribirse.
                  </p>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    <li>Debes agregar al menos un usuario o familiar autorizado</li>
                    <li>Puedes buscar usuarios por email/RUT o familiares por RUT/nombre</li>
                    <li>Solo entidades autorizadas verán este taller</li>
                  </ul>
                </div>
              }
              type="warning"
              showIcon
            />

            {!disabled && (
              <Card size="small" title="Buscar usuarios o familiares autorizados">
                <div className="mb-3">
                  <Select
                    value={tipoBusqueda}
                    onChange={(value) => {
                      setTipoBusqueda(value);
                      setSearchQuery('');
                      setUsuarioEncontrado(null);
                      setFamiliarEncontrado(null);
                      setFamiliaresEncontrados([]);
                    }}
                    style={{ width: '100%' }}
                    size="large"
                  >
                    <Select.Option value="usuario">
                      <UserOutlined /> Buscar Usuario Principal (por email o RUT)
                    </Select.Option>
                    <Select.Option value="familiar">
                      <TeamOutlined /> Buscar Familiar/Carga (por RUT o nombre)
                    </Select.Option>
                  </Select>
                </div>

                <Space.Compact style={{ width: '100%' }} size="large">
                  <Input
                    placeholder={
                      tipoBusqueda === 'usuario' 
                        ? "Buscar por email o RUT" 
                        : "Buscar por RUT o nombre del familiar"
                    }
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onPressEnter={buscarEntidad}
                    prefix={<SearchOutlined />}
                    disabled={searchLoading}
                  />
                  <Button 
                    type="primary" 
                    onClick={buscarEntidad}
                    loading={searchLoading}
                    disabled={!searchQuery.trim()}
                  >
                    {searchLoading ? 'Buscando...' : 'Buscar'}
                  </Button>
                </Space.Compact>

                {usuarioEncontrado && (
                  <div className="mt-3 p-3 border border-blue-300 bg-blue-50 rounded">
                    <div className="flex items-center justify-between">
                      <div>
                        <Tag color="blue">USUARIO</Tag>
                        <p className="font-medium">{usuarioEncontrado.nombre}</p>
                        <p className="text-sm text-gray-600">
                          {usuarioEncontrado.email} 
                          {usuarioEncontrado.rut && ` • RUT: ${usuarioEncontrado.rut}`}
                        </p>
                      </div>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => agregarEntidad()}
                      >
                        Agregar
                      </Button>
                    </div>
                  </div>
                )}

                {familiarEncontrado && (
                  <div className="mt-3 p-3 border border-purple-300 bg-purple-50 rounded">
                    <div className="flex items-center justify-between">
                      <div>
                        <Tag color="purple">FAMILIAR</Tag>
                        <p className="font-medium">{familiarEncontrado.nombre}</p>
                        <p className="text-sm text-gray-600">
                          RUT: {familiarEncontrado.rut}
                          {familiarEncontrado.email && ` • ${familiarEncontrado.email}`}
                        </p>
                      </div>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => agregarEntidad()}
                      >
                        Agregar
                      </Button>
                    </div>
                  </div>
                )}

                {familiaresEncontrados.length > 0 && (
                  <div className="mt-3">
                    <Alert
                      message={`Se encontraron ${familiaresEncontrados.length} familiares`}
                      description="Selecciona el familiar que deseas agregar"
                      type="info"
                      showIcon
                      className="mb-2"
                    />
                    <List
                      size="small"
                      bordered
                      dataSource={familiaresEncontrados}
                      renderItem={(familiar) => (
                        <List.Item
                          actions={[
                            <Button
                              type="primary"
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() => agregarEntidad(familiar)}
                            >
                              Agregar
                            </Button>
                          ]}
                        >
                          <List.Item.Meta
                            avatar={<Tag color="purple">FAMILIAR</Tag>}
                            title={familiar.nombre}
                            description={`RUT: ${familiar.rut}${familiar.email ? ` • ${familiar.email}` : ''}`}
                          />
                        </List.Item>
                      )}
                    />
                  </div>
                )}
              </Card>
            )}

            <Card 
              size="small" 
              title={`Autorizados (${usuariosAutorizados.length})`}
              extra={
                usuariosAutorizados.length === 0 && (
                  <Tag color="orange">Debe tener al menos 1</Tag>
                )
              }
            >
              {usuariosAutorizados.length === 0 ? (
                <Alert
                  message="No hay autorizados"
                  description="Las academias deben tener al menos un usuario o familiar autorizado"
                  type="warning"
                  showIcon
                />
              ) : (
                <div className="space-y-2">
                  {usuariosAutorizados.map((entidad) => (
                    <div
                      key={entidad.id}
                      className="flex items-center justify-between p-3 border rounded hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        {entidad.tipo === 'usuario' ? (
                          <Tag color="blue">USUARIO</Tag>
                        ) : (
                          <Tag color="purple">FAMILIAR</Tag>
                        )}
                        <div>
                          <p className="font-medium">{entidad.nombre}</p>
                          <p className="text-sm text-gray-600">
                            {entidad.email || `RUT: ${entidad.rut}`}
                          </p>
                        </div>
                      </div>
                      {!disabled && (
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => eliminarEntidad(entidad.id, entidad.nombre)}
                        >
                          Eliminar
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}
      </Card>

      <Card title={<span>💰 Tributación</span>}>
        <Form.Item
          name="tieneIVA"
          valuePropName="checked"
          tooltip="Si activas esta opción, las boletas se emitirán como afectas a IVA (19%). Por defecto los talleres son exentos."
        >
          <Checkbox disabled={disabled}>
            <span className="font-medium">
              Este taller está afecto a IVA (19%)
            </span>
          </Checkbox>
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, curr) =>
          prev.tieneIVA !== curr.tieneIVA
        }>
          {({ getFieldValue }) => {
            const tieneIVA = getFieldValue('tieneIVA');
            return (
              <Alert
                message={tieneIVA ? 'Boleta afecta (con IVA)' : 'Boleta exenta'}
                description={
                  tieneIVA
                    ? 'Se emitirá Boleta Electrónica Afecta (SII tipo 39). El precio publicado se interpreta como bruto y se calculará el neto dividiendo por 1,19.'
                    : 'Se emitirá Boleta Electrónica Exenta (SII tipo 41). El precio publicado se traspasa íntegro al documento, sin cálculo de IVA.'
                }
                type={tieneIVA ? 'warning' : 'info'}
                showIcon
                className="mt-2"
              />
            );
          }}
        </Form.Item>
      </Card>

      {/* Restricción Tarjeta Vecino - continúa igual... */}
      <Card title={
        <span>
          <SafetyCertificateOutlined className="mr-2" />
          Restricciones de acceso
        </span>
      }>
        <Form.Item
          name="soloTarjetaVecino"
          valuePropName="checked"
          tooltip="Si activas esta opción, solo los beneficiarios de Tarjeta Vecino podrán inscribirse en este taller"
        >
          <Checkbox disabled={disabled}>
            <span className="font-medium">
              Taller exclusivo para beneficiarios de Tarjeta Vecino
            </span>
          </Checkbox>
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, curr) =>
          prev.soloTarjetaVecino !== curr.soloTarjetaVecino
        }>
          {({ getFieldValue }) => {
            const soloTarjetaVecino = getFieldValue('soloTarjetaVecino');

            if (soloTarjetaVecino) {
              return (
                <Alert
                  message="⚠️ Taller con acceso restringido"
                  description={
                    <div className="space-y-2">
                      <p>
                        Este taller estará disponible <strong>únicamente para beneficiarios de Tarjeta Vecino</strong>.
                      </p>
                      <ul className="list-disc list-inside text-sm space-y-1">
                        <li>Los usuarios deberán validar su RUT antes de inscribirse</li>
                        <li>Solo quienes sean beneficiarios activos podrán completar la inscripción</li>
                        <li>El sistema verificará automáticamente el beneficio al momento de la compra</li>
                      </ul>
                    </div>
                  }
                  type="warning"
                  showIcon
                  className="mt-2"
                />
              );
            }

            return (
              <Alert
                message="Acceso abierto"
                description="Este taller estará disponible para todos los vecinos, sin restricciones"
                type="info"
                showIcon
                className="mt-2"
              />
            );
          }}
        </Form.Item>
      </Card>

      {/* Torniquete: los talleres de piscina abren antes (pedido de la Corporación,
          26-10-2026). Va por taller porque ningún recinto está cargado como piscina. */}
      <Card title="🚪 Acceso al torniquete">
        <Form.Item
          name="accesoPiscina"
          valuePropName="checked"
          tooltip="El torniquete deja entrar a los inscritos 1 hora antes de la clase, en vez de los 30 minutos generales"
          className="mb-0"
        >
          <Checkbox disabled={disabled}>
            <span className="font-medium">
              Taller de piscina: acceso desde 1 hora antes de la clase
            </span>
          </Checkbox>
        </Form.Item>
      </Card>

      {/* Correo al titular 3 días antes de que termine lo pagado (pedido del cliente,
          oct-2026, para la membresía del gimnasio Ñuñoa Plaza). */}
      <Card title="✉️ Avisos por correo">
        <Form.Item
          name="avisoVencimiento"
          valuePropName="checked"
          tooltip="Se manda al titular de la compra (quien pagó). No se manda si la persona ya compró el período siguiente."
          className="mb-0"
        >
          <Checkbox>
            <span className="font-medium">
              Avisar por correo 3 días antes de que venza el plan
            </span>
          </Checkbox>
        </Form.Item>
      </Card>

      {/* Rangos de edad - código sin cambios */}
      <Card title="👥 Rango de edades permitidas">
        <Alert
          message="Rango de edad"
          description="Define las edades mínima y máxima permitidas para inscribirse. Déjalos vacíos si no hay restricciones."
          type="info"
          showIcon
          className="mb-4"
        />

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="Edad mínima"
            name="edadMin"
            tooltip="Edad mínima requerida para inscribirse"
          >
            <InputNumber
              size="large"
              className="w-full"
              min={0}
              max={100}
              placeholder="Ej: 18"
            />
          </Form.Item>

          <Form.Item
            label="Edad máxima"
            name="edadMax"
            tooltip="Edad máxima permitida para inscribirse"
          >
            <InputNumber
              size="large"
              className="w-full"
              min={0}
              max={100}
              placeholder="Ej: 65"
            />
          </Form.Item>
        </div>

        <Form.Item noStyle shouldUpdate={(prev, curr) => 
          prev.edadMin !== curr.edadMin || prev.edadMax !== curr.edadMax
        }>
          {({ getFieldValue }) => {
            const edadMin = getFieldValue('edadMin');
            const edadMax = getFieldValue('edadMax');

            if (edadMin || edadMax) {
              return (
                <Alert
                  message="Rango configurado"
                  description={
                    <div>
                      {edadMin && edadMax ? (
                        <>De <strong>{edadMin}</strong> a <strong>{edadMax}</strong> años</>
                      ) : edadMin ? (
                        <>Desde <strong>{edadMin}</strong> años en adelante</>
                      ) : (
                        <>Hasta <strong>{edadMax}</strong> años</>
                      )}
                    </div>
                  }
                  type="success"
                  showIcon
                  className="mt-2"
                />
              );
            }

            return (
              <Alert
                message="Sin restricción de edad"
                description="Todas las edades pueden inscribirse"
                type="info"
                showIcon
                className="mt-2"
              />
            );
          }}
        </Form.Item>
      </Card>

      {/* Gestión de cupos - código sin cambios */}
      <Card title="🎫 Gestión de cupos">
        <Form.Item
          name="liberarCuposPorBaja"
          valuePropName="checked"
          tooltip="Si se activa, cuando un usuario se dé de baja, su cupo quedará disponible automáticamente"
        >
          <Checkbox disabled={disabled}>
            <span className="font-medium">
              Liberar cupos automáticamente cuando un usuario se dé de baja
            </span>
          </Checkbox>
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, curr) =>
          prev.liberarCuposPorBaja !== curr.liberarCuposPorBaja
        }>
          {({ getFieldValue }) => {
            const liberarCupos = getFieldValue('liberarCuposPorBaja');

            if (liberarCupos) {
              return (
                <Alert
                  message="✅ Liberación automática activada"
                  description="Los cupos se liberarán automáticamente cuando un usuario se dé de baja, permitiendo que otros usuarios puedan inscribirse"
                  type="success"
                  showIcon
                  className="mt-2"
                />
              );
            }

            return (
              <Alert
                message="Liberación manual"
                description="Los cupos NO se liberarán automáticamente. Deberás gestionarlos manualmente desde el panel de administración"
                type="info"
                showIcon
                className="mt-2"
              />
            );
          }}
        </Form.Item>
      </Card>
    </div>
  );
}
